/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {AppContext} from "../../../misc/appContext";
import {SelectableReviewProduct} from '../../../../util/types';
import {getSelectableReviewProducts} from "../../../../util/requestSender";
import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../../util/utils";
import {useNavigate} from 'react-router';
import {REVIEW_ANALYTICS_ROUTE} from "../../../../util/constants";
import FeedProductSelect from "./feedProductSelect";

const LIMIT = 20;
const DEBOUNCE = 500;

export default function FeedProductSelectController() {
    const {account} = useContext(AppContext).accountContext;
    const [products, setProducts] = useState<Array<SelectableReviewProduct>>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [total, setTotal] = useState(0);
    const navigate = useNavigate();
    const abortController = useRef<AbortController | null>(null);
    const searchTimeout = useRef<number | undefined>(undefined);

    function onOpen() {
        if (open) return onClose();
        onSearch()();
        setOpen(true);
        // Focus search field
        window.setTimeout(() => {
            document.getElementById('search-field-input')?.focus();
        }, 200);
    }

    function onClose() {
        setOpen(false);
        // Delay further state changes by 200 ms to wait for the closing transition.
        // We prevent flickering in the popup this way
        window.setTimeout(() => {
            setProducts([]);
            setLoading(false);
            setLoadingMore(false);
            setSearch('');
            setTotal(0);
        }, 200);
    }

    function onChangeSearch(value: string) {
        clearSearchTimeout();
        setSearch(value);
        searchTimeout.current = window.setTimeout(() => {
            onSearch(value)();
        }, DEBOUNCE);
    }

    function onClickProduct(product: SelectableReviewProduct) {
        return function() {
            navigate(`${REVIEW_ANALYTICS_ROUTE}/${product.ean}?analyse=1`);
        }
    }

    function onSearch(_search?: string) {
        return function() {
            console.debug(_search);
            clearSearchTimeout();
            abortController.current?.abort();
            if (!account) return;
            setProducts([]);
            setLoading(true);
            abortController.current = getSelectableReviewProducts({
                data: {
                    search: _search ?? search, page: 0, limit: LIMIT, account_id: account.id,
                }, callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        setProducts(data.products);
                        setTotal(data.total);
                    } else showDialog(createErrorMessage(data.message ?? 'Het laden van de producten is niet gelukt.'),
                        'Laden mislukt');
                }
            });
        }
    }

    function onLoadMore(startIndex: number) {
        return new Promise<void>((resolve, reject) => {
            if (!account) return reject();
            setLoadingMore(true);
            const page = Math.floor(startIndex / LIMIT);
            getSelectableReviewProducts({
                data: {
                    account_id: account.id, limit: LIMIT, page, search
                }, callback: (data: ResponseData) => {
                    setLoadingMore(false);
                    if (data.success) setProducts(products.concat(data.products));
                    resolve();
                }
            })
        });
    }

    function clearSearchTimeout() {
        window.clearTimeout(searchTimeout.current);
        searchTimeout.current = undefined;
    }

    useEffect(() => {
        return function() {
            clearSearchTimeout();
            abortController.current?.abort();
        }
    }, []);

    return (
        <FeedProductSelect onSearch={onSearch()} onChangeSearch={onChangeSearch} onClickProduct={onClickProduct}
                           onLoadMore={onLoadMore} onOpen={onOpen} onClose={onClose} loadingMore={loadingMore}
                           search={search} loading={loading} products={products} total={total} open={open}/>
    );
}